import { ChangeEvent, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Avatar,
  Box,
  Button,
  Card,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Scrollbar } from '@/components';
import Iconify from '@/components/Iconify';
import Label from '@/components/Label';
import { SkeletonLoadingTable } from '@/components/skeleton';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
} from '@/components/table';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import useTable from '@/hooks/useTable';
import {
  getCommunicationLog,
  getUserCommunicationLogs,
} from '@/features/users/shared/api';
import type {
  CommunicationLog,
  CommunicationLogFilters,
  GetCommunicationLogsParams,
} from '@/features/users/shared/types';

const COMMUNICATIONS_TABLE_HEAD = [
  { id: 'createdAt', label: 'Created', minWidth: 170 },
  { id: 'status', label: 'Status', minWidth: 100 },
  { id: 'provider', label: 'Provider', minWidth: 110 },
  { id: 'channel', label: 'Channel', minWidth: 100 },
  { id: 'recipientEmail', label: 'Recipient', minWidth: 220 },
  { id: 'subject', label: 'Subject', minWidth: 260 },
  { id: 'messageId', label: 'Message ID', minWidth: 180 },
  { id: 'dispatchId', label: 'Dispatch ID', minWidth: 180 },
  { id: '', label: '', width: 64 },
];

const DEFAULT_FILTERS: CommunicationLogFilters = {
  email: '',
  status: '',
  provider: '',
  channel: '',
  from: '',
  to: '',
};

const STATUS_OPTIONS = ['pending', 'sent', 'failed', 'partial', 'skipped'];
const PROVIDER_OPTIONS = ['smtp', 'onesignal', 'firebase'];
const CHANNEL_OPTIONS = ['email', 'push'];

type LabelColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';

const formatDate = (value: string | null) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return format(date, 'MMM d, yyyy h:mm a');
};

const statusColor = (status: string): LabelColor => {
  switch (status.toLowerCase()) {
    case 'sent':
      return 'success';
    case 'failed':
      return 'error';
    case 'partial':
      return 'info';
    case 'skipped':
      return 'default';
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
};

const titleCase = (value: string | null | undefined) => {
  if (!value) return '-';

  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const stringifyValue = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return '-';

  if (typeof value === 'string') return value;

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const formatRecipients = (value: unknown[]) => {
  if (!value.length) return '-';

  return value
    .map((item) => (typeof item === 'string' ? item : stringifyValue(item)))
    .join(', ');
};

const formatTokenHash = (value: unknown) =>
  typeof value === 'string' ? value : stringifyValue(value);

type TokenDeliveryDetail = {
  hash: string;
  status: string;
  error: string | null;
};

const getEventTime = (row: CommunicationLog) =>
  row.sentAt ?? row.failedAt ?? row.createdAt;

const hasRecipients = (value: unknown[]) => value.length > 0;

const isFiltersApplied = (filters: CommunicationLogFilters) =>
  Object.values(filters).some(Boolean);

const getRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const getStringField = (
  record: Record<string, unknown>,
  keys: string[]
): string | null => {
  const value = keys
    .map((key) => record[key])
    .find((item) => typeof item === 'string' && item.trim());

  return typeof value === 'string' ? value : null;
};

const getBooleanField = (
  record: Record<string, unknown>,
  keys: string[]
): boolean | null => {
  const value = keys
    .map((key) => record[key])
    .find((item) => typeof item === 'boolean');

  return typeof value === 'boolean' ? value : null;
};

const collectRecordsWithTokenHash = (
  value: unknown
): Record<string, unknown>[] => {
  const results: Record<string, unknown>[] = [];

  const visit = (current: unknown) => {
    if (Array.isArray(current)) {
      current.forEach(visit);
      return;
    }

    const record = getRecord(current);
    if (!Object.keys(record).length) return;

    const hash = getStringField(record, [
      'token_hash',
      'tokenHash',
      'token',
      'hash',
    ]);

    if (hash) {
      results.push(record);
      return;
    }

    Object.values(record).forEach(visit);
  };

  visit(value);

  return results;
};

const getTokenDeliveryDetails = (
  log: CommunicationLog
): TokenDeliveryDetail[] => {
  const responseRecords = collectRecordsWithTokenHash(log.providerResponse);
  const responseByHash = new Map(
    responseRecords.map((record) => {
      const hash =
        getStringField(record, ['token_hash', 'tokenHash', 'token', 'hash']) ??
        '';

      return [hash, record];
    })
  );

  return log.tokenHashes.map((tokenHash) => {
    const hash = formatTokenHash(tokenHash);
    const record = responseByHash.get(hash) ?? {};
    const error = getStringField(record, [
      'error',
      'error_message',
      'errorMessage',
      'reason',
      'code',
    ]);
    const success = getBooleanField(record, ['success', 'sent', 'delivered']);
    const rawStatus = getStringField(record, ['status', 'delivery_status']);

    const status = rawStatus
      ? titleCase(rawStatus)
      : success === true
      ? 'Delivered'
      : success === false || error
      ? 'Failed'
      : 'Unknown';

    return { hash, status, error };
  });
};

const getSenderEmail = (payload: unknown): string | null => {
  const record = getRecord(payload);

  const directSender = getStringField(record, [
    'email_from_address',
    'emailFromAddress',
    'from_email',
    'fromEmail',
    'sender_email',
    'senderEmail',
    'reply_to',
    'replyTo',
    'from',
    'sender',
  ]);

  if (directSender) return directSender;

  const nestedRecords = [
    getRecord(record.from),
    getRecord(record.sender),
    getRecord(record.mail),
    getRecord(record.mail_options),
    getRecord(record.mailOptions),
    getRecord(record.smtp),
  ];

  for (const nestedRecord of nestedRecords) {
    const nestedSender = getStringField(nestedRecord, [
      'email',
      'address',
      'email_address',
      'emailAddress',
      'from',
    ]);

    if (nestedSender) return nestedSender;
  }

  return null;
};

const getNestedString = (
  value: unknown,
  paths: Array<string[]>
): string | null => {
  for (const path of paths) {
    let current: unknown = value;

    for (const key of path) {
      current = getRecord(current)[key];
    }

    if (typeof current === 'string' && current.trim()) {
      return current;
    }
  }

  return null;
};

const getPushContent = (payload: unknown) => {
  const title =
    getNestedString(payload, [
      ['headings', 'en'],
      ['headings'],
      ['notification', 'title'],
      ['message', 'notification', 'title'],
      ['data', 'title'],
      ['title'],
    ]) ?? null;

  const body =
    getNestedString(payload, [
      ['contents', 'en'],
      ['contents'],
      ['notification', 'body'],
      ['message', 'notification', 'body'],
      ['data', 'body'],
      ['body'],
      ['message'],
    ]) ?? null;

  return { title, body };
};

const getRecipientLabel = (log: CommunicationLog) => {
  if (log.channel === 'push') {
    const recipientType = log.recipientType
      ? titleCase(log.recipientType)
      : 'Push';
    const identifier = log.recipientIdentifier
      ? ` ${log.recipientIdentifier}`
      : '';

    return `${recipientType}${identifier}`;
  }

  return log.recipientEmail ?? '-';
};

const getCommunicationSummary = (log: CommunicationLog) => {
  if (log.channel === 'push') {
    const content = getPushContent(log.providerPayload);
    return content.title ?? content.body ?? log.messageId ?? '-';
  }

  return log.subject ?? '-';
};

type UserCommunicationsToolbarProps = {
  filters: CommunicationLogFilters;
  onFilters: (name: keyof CommunicationLogFilters, value: string) => void;
  onResetFilters: VoidFunction;
  isFiltered: boolean;
};

function UserCommunicationsToolbar({
  filters,
  onFilters,
  onResetFilters,
  isFiltered,
}: UserCommunicationsToolbarProps) {
  const handleInputChange =
    (name: keyof CommunicationLogFilters) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      onFilters(name, event.target.value);
    };

  return (
    <Grid container spacing={2} sx={{ py: 2.5, px: 3 }}>
      <Grid item xs={12} md={4} lg={3}>
        <TextField
          value={filters.email}
          onChange={handleInputChange('email')}
          fullWidth
          size="small"
          placeholder="Search recipient email"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-outline" width={20} />
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={2}>
        <TextField
          select
          fullWidth
          size="small"
          label="Status"
          value={filters.status}
          onChange={handleInputChange('status')}
        >
          <MenuItem value="">All</MenuItem>
          {STATUS_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {titleCase(option)}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={2}>
        <TextField
          select
          fullWidth
          size="small"
          label="Provider"
          value={filters.provider}
          onChange={handleInputChange('provider')}
        >
          <MenuItem value="">All</MenuItem>
          {PROVIDER_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {titleCase(option)}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={2}>
        <TextField
          select
          fullWidth
          size="small"
          label="Channel"
          value={filters.channel}
          onChange={handleInputChange('channel')}
        >
          <MenuItem value="">All</MenuItem>
          {CHANNEL_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {titleCase(option)}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={1.5}>
        <TextField
          fullWidth
          size="small"
          label="From"
          type="date"
          value={filters.from}
          onChange={handleInputChange('from')}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={1.5}>
        <TextField
          fullWidth
          size="small"
          label="To"
          type="date"
          value={filters.to}
          onChange={handleInputChange('to')}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>

      {isFiltered && (
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              color="inherit"
              onClick={onResetFilters}
              startIcon={<Iconify icon="eva:close-circle-outline" />}
            >
              Reset filters
            </Button>
          </Stack>
        </Grid>
      )}
    </Grid>
  );
}

type DetailItemProps = {
  label: string;
  value: unknown;
};

function DetailItem({ label, value }: DetailItemProps) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontSize: 13, lineHeight: 1.35, wordBreak: 'break-word' }}
      >
        {stringifyValue(value)}
      </Typography>
    </Stack>
  );
}

type HtmlBodyPreviewProps = {
  value: string | null;
};

function HtmlBodyPreview({ value }: HtmlBodyPreviewProps) {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        p: 2,
        bgcolor: 'background.neutral',
      }}
    >
      {value ? (
        <Box
          component="iframe"
          title="Rendered HTML email body"
          srcDoc={value}
          sandbox=""
          sx={{
            width: 1,
            height: 1,
            minHeight: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'common.white',
          }}
        />
      ) : (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.neutral',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        </Box>
      )}
    </Box>
  );
}

type EmailMetaStripProps = {
  log: CommunicationLog;
};

function EmailMetaStrip({ log }: EmailMetaStripProps) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.neutral',
      }}
    >
      <Grid container columnSpacing={2} rowSpacing={1.25}>
        <Grid item xs={4}>
          <DetailItem label="Status" value={titleCase(log.status)} />
        </Grid>
        <Grid item xs={4}>
          <DetailItem label="Provider" value={titleCase(log.provider)} />
        </Grid>
        <Grid item xs={4}>
          <DetailItem label="Channel" value={titleCase(log.channel)} />
        </Grid>
        {log.tokenCount !== null && (
          <Grid item xs={4}>
            <DetailItem label="Tokens" value={log.tokenCount} />
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <DetailItem label="Message ID" value={log.messageId} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DetailItem label="Dispatch ID" value={log.dispatchId} />
        </Grid>
        {log.errorMessage && (
          <Grid item xs={12}>
            <DetailItem label="Error message" value={log.errorMessage} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

const emailAvatarLabel = (value: string | null | undefined) => {
  const parts = titleCase(value)
    .split(' ')
    .filter((part) => part && part !== '-');

  if (!parts.length) return 'EM';

  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase();
};

type EmailMessageHeaderProps = {
  log: CommunicationLog;
};

function EmailMessageHeader({ log }: EmailMessageHeaderProps) {
  const toRecipients = hasRecipients(log.to)
    ? formatRecipients(log.to)
    : log.recipientEmail ?? '-';
  const senderEmail = getSenderEmail(log.providerPayload);

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <Avatar
        sx={{
          width: 44,
          height: 44,
          bgcolor: 'grey.900',
          color: 'common.white',
          fontWeight: 700,
        }}
      >
        {emailAvatarLabel(senderEmail ?? log.channel)}
      </Avatar>

      <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          sx={{ minWidth: 0 }}
        >
          <Typography variant="subtitle1" sx={{ lineHeight: 1.25 }}>
            {senderEmail ? `From: ${senderEmail}` : 'Outbound email'}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: 'nowrap' }}
          >
            {formatDate(getEventTime(log))}
          </Typography>
        </Stack>

        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {log.subject ?? '-'}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          To: {toRecipients}
        </Typography>

        {hasRecipients(log.cc) && (
          <Typography variant="body2" color="text.secondary">
            CC: {formatRecipients(log.cc)}
          </Typography>
        )}

        {hasRecipients(log.bcc) && (
          <Typography variant="body2" color="text.secondary">
            BCC: {formatRecipients(log.bcc)}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

function PushMessageHeader({ log }: EmailMessageHeaderProps) {
  const content = getPushContent(log.providerPayload);
  const recipient = getRecipientLabel(log);

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <Avatar
        sx={{
          width: 44,
          height: 44,
          bgcolor: 'grey.900',
          color: 'common.white',
          fontWeight: 700,
        }}
      >
        PU
      </Avatar>

      <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          sx={{ minWidth: 0 }}
        >
          <Typography variant="subtitle1" sx={{ lineHeight: 1.25 }}>
            Push notification
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: 'nowrap' }}
          >
            {formatDate(getEventTime(log))}
          </Typography>
        </Stack>

        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {content.title ?? 'Untitled push notification'}
        </Typography>

        {content.body && (
          <Typography variant="body2" color="text.secondary">
            {content.body}
          </Typography>
        )}

        <Typography variant="body2" color="text.secondary">
          To: {recipient}
          {log.tokenCount !== null ? ` (${log.tokenCount} tokens)` : ''}
        </Typography>
      </Stack>
    </Stack>
  );
}

function PushBodyPreview({ log }: EmailMessageHeaderProps) {
  const content = getPushContent(log.providerPayload);
  const tokenDeliveryDetails = getTokenDeliveryDetails(log);
  const hasTokenHashes = tokenDeliveryDetails.length > 0;

  return (
    <Box sx={{ p: 2, bgcolor: 'background.neutral', flex: 1 }}>
      <Box
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        <Stack spacing={1}>
          <Label color={statusColor(log.status)}>{titleCase(log.status)}</Label>
          <Typography variant="subtitle1">
            {content.title ?? 'Push notification'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {content.body ?? 'No push content recorded.'}
          </Typography>

          {hasTokenHashes && (
            <Stack spacing={1} sx={{ pt: 1 }}>
              <Typography variant="subtitle2">Device token hashes</Typography>
              <Stack spacing={0.75}>
                {tokenDeliveryDetails.map((tokenDetail, index) => (
                  <Box
                    key={`${tokenDetail.hash}-${index}`}
                    sx={{
                      px: 1,
                      py: 0.75,
                      borderRadius: 1,
                      bgcolor: 'background.neutral',
                      color: 'text.primary',
                    }}
                  >
                    <Stack spacing={0.5}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box
                          component="code"
                          sx={{
                            fontFamily:
                              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                            fontSize: 12,
                            lineHeight: 1.35,
                            wordBreak: 'break-all',
                          }}
                        >
                          {tokenDetail.hash}
                        </Box>
                        <Label
                          color={
                            tokenDetail.status.toLowerCase() === 'delivered'
                              ? 'success'
                              : tokenDetail.status.toLowerCase() === 'failed'
                              ? 'error'
                              : 'default'
                          }
                        >
                          {tokenDetail.status}
                        </Label>
                      </Stack>
                      {tokenDetail.error && (
                        <Typography variant="caption" color="error">
                          {tokenDetail.error}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

type CommunicationLogDetailsDrawerProps = {
  open: boolean;
  log: CommunicationLog | null;
  isLoading: boolean;
  onClose: VoidFunction;
};

function CommunicationLogDetailsDrawer({
  open,
  log,
  isLoading,
  onClose,
}: CommunicationLogDetailsDrawerProps) {
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: { xs: '100vw', sm: 560, md: 680 },
          maxWidth: '100vw',
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, py: 2 }}
        >
          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <Typography variant="h6">
              {log?.channel === 'push' ? 'Push notification' : 'Email'}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {log ? getCommunicationSummary(log) : '-'}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} aria-label="Close communication log">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Divider />

        {isLoading && !log ? (
          <Stack sx={{ p: 2.5 }}>
            <SkeletonLoadingTable />
          </Stack>
        ) : log ? (
          <>
            <EmailMetaStrip log={log} />
            {log.channel === 'push' ? (
              <>
                <PushMessageHeader log={log} />
                <PushBodyPreview log={log} />
              </>
            ) : (
              <>
                <EmailMessageHeader log={log} />
                <HtmlBodyPreview value={log.htmlBody} />
              </>
            )}
          </>
        ) : (
          <Scrollbar>
            <Stack sx={{ p: 2.5 }}>
              <Typography variant="body2" color="text.secondary">
                Select a communication log to view details.
              </Typography>
            </Stack>
          </Scrollbar>
        )}
      </Box>
    </Drawer>
  );
}

type CommunicationLogTableRowProps = {
  row: CommunicationLog;
  onViewDetails: (row: CommunicationLog) => void;
};

function CommunicationLogTableRow({
  row,
  onViewDetails,
}: CommunicationLogTableRowProps) {
  return (
    <TableRow hover>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {formatDate(getEventTime(row))}
      </TableCell>
      <TableCell>
        <Label color={statusColor(row.status)}>{titleCase(row.status)}</Label>
      </TableCell>
      <TableCell>{titleCase(row.provider)}</TableCell>
      <TableCell>{titleCase(row.channel)}</TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {getRecipientLabel(row)}
        </Typography>
      </TableCell>
      <TableCell>
        <Tooltip title={getCommunicationSummary(row)} placement="top" arrow>
          <Typography
            variant="body2"
            sx={{
              maxWidth: 320,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {getCommunicationSummary(row)}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {row.messageId ?? '-'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {row.dispatchId ?? '-'}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Tooltip title="View communication log">
          <IconButton
            size="small"
            onClick={() => onViewDetails(row)}
            aria-label={`View communication log ${row.id}`}
          >
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

export default function UserCommunications() {
  const { userId } = useParams();
  const { errorMsg } = useSnackbarMsg();
  const [filters, setFilters] =
    useState<CommunicationLogFilters>(DEFAULT_FILTERS);
  const [selectedLog, setSelectedLog] = useState<CommunicationLog | null>(null);

  const table = useTable({
    defaultRowsPerPage: 25,
  });

  const params = useMemo<GetCommunicationLogsParams>(() => {
    const nextParams: GetCommunicationLogsParams = {
      limit: table.rowsPerPage,
      offset: table.page * table.rowsPerPage,
    };

    if (filters.email) nextParams.email = filters.email;
    if (filters.status) nextParams.status = filters.status;
    if (filters.provider) nextParams.provider = filters.provider;
    if (filters.channel) nextParams.channel = filters.channel;
    if (filters.from) nextParams.from = filters.from;
    if (filters.to) nextParams.to = filters.to;

    return nextParams;
  }, [
    filters.channel,
    filters.email,
    filters.from,
    filters.provider,
    filters.status,
    filters.to,
    table.page,
    table.rowsPerPage,
  ]);

  const communicationsQuery = useQuery({
    queryKey: ['userCommunications', userId, params],
    queryFn: () => getUserCommunicationLogs(String(userId), params),
    enabled: !!userId,
    onError: (error) => {
      errorMsg(error, 'Unable to load communication logs');
    },
  });

  const selectedLogId = selectedLog?.id;
  const detailQuery = useQuery({
    queryKey: ['communicationLog', selectedLogId],
    queryFn: () => getCommunicationLog(String(selectedLogId)),
    enabled: !!selectedLogId,
    onError: (error) => {
      errorMsg(error, 'Unable to load communication log details');
    },
  });

  const logs = communicationsQuery.data?.data ?? [];
  const total = communicationsQuery.data?.meta.total ?? logs.length;
  const drawerLog = detailQuery.data ?? selectedLog;
  const denseHeight = table.dense ? 52 : 72;
  const isFiltered = isFiltersApplied(filters);
  const isNotFound = !logs.length;

  const handleFilters = (
    name: keyof CommunicationLogFilters,
    value: string
  ) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    table.setPage(0);
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    table.setPage(0);
  };

  const handleCloseDrawer = () => {
    setSelectedLog(null);
  };

  return (
    <Card>
      {communicationsQuery.isLoading ? (
        <SkeletonLoadingTable />
      ) : (
        <>
          <UserCommunicationsToolbar
            filters={filters}
            onFilters={handleFilters}
            onResetFilters={handleResetFilters}
            isFiltered={isFiltered}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 1250, position: 'relative' }}>
              <Table size={table.dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  headLabel={COMMUNICATIONS_TABLE_HEAD}
                  rowCount={logs.length}
                  numSelected={table.selected.length}
                />
                <TableBody>
                  {logs.map((row) => (
                    <CommunicationLogTableRow
                      key={row.id}
                      row={row}
                      onViewDetails={setSelectedLog}
                    />
                  ))}

                  {logs.length < table.rowsPerPage && (
                    <TableEmptyRows
                      height={denseHeight}
                      emptyRows={table.rowsPerPage - logs.length}
                    />
                  )}

                  <TableNoData
                    isNotFound={isNotFound}
                    title={
                      isFiltered
                        ? 'No communication logs found'
                        : 'No communication logs available'
                    }
                  />
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={total}
              rowsPerPage={table.rowsPerPage}
              page={table.page}
              onPageChange={table.onChangePage}
              onRowsPerPageChange={table.onChangeRowsPerPage}
            />

            <FormControlLabel
              control={
                <Switch checked={table.dense} onChange={table.onChangeDense} />
              }
              label="Dense"
              sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
            />
          </Box>

          <CommunicationLogDetailsDrawer
            open={!!selectedLog}
            log={drawerLog}
            isLoading={detailQuery.isLoading}
            onClose={handleCloseDrawer}
          />
        </>
      )}
    </Card>
  );
}
